import React, { FC, useState, useEffect, useRef } from 'react';
import { StateMachineInputType, Rive, EventType } from '@rive-app/react-webgl2';

import styles from './StateMachineInputs.module.css';
import { CustomSwitch } from 'components/about/Switch';
import { ValueCallback } from 'types/callbacks';
import { insertIf, join } from 'lib/helpers';
import { StateMachineInput } from 'types/rive';
import HoldButton from 'components/common/HoldButton';

type UseInputsProps = {
  rive: Rive | null;
  stateMachineName: string;
  isReady: boolean;
};

export function useInputs({
  rive,
  stateMachineName,
  isReady,
}: UseInputsProps): StateMachineInput[] | null {
  const [inputs, setInputs] = useState<StateMachineInput[] | null>(null);

  useEffect(() => {
    if (rive && isReady) {
      setInputs(rive.stateMachineInputs(stateMachineName));
    }
  }, [rive, isReady]);

  return inputs;
}

type StateMachineInputsProps = {
  rive: Rive;
  inputs: StateMachineInput[];
  onDragChange?: ValueCallback<boolean>;
};

const StateMachineInputs = ({
  rive,
  inputs,
  onDragChange,
}: StateMachineInputsProps) => {
  const defaultOnDragChange = () => {};

  const inputComponents = inputs.map((input, i) => (
    <InputFactory
      key={i + input.name}
      rive={rive}
      input={input}
      onDragChange={onDragChange ?? defaultOnDragChange}
    />
  ));

  return <>{inputComponents}</>;
};

export default StateMachineInputs;

type InputFactoryProps = {
  rive: Rive;
  input: StateMachineInput;
  onDragChange: (isDragging: boolean, name: string) => void;
};

const InputFactory = ({ rive, input, onDragChange }: InputFactoryProps) => {
  switch (input.type) {
    case StateMachineInputType.Number:
      return (
        <NumberInput
          rive={rive}
          input={input}
          onDragChange={(isDragging: boolean) =>
            onDragChange(isDragging, input.name)
          }
        />
      );
    case StateMachineInputType.Boolean:
      return <BooleanInput rive={rive} input={input} />;
    case StateMachineInputType.Trigger:
    default:
      return <TriggerInput input={input} />;
  }
};

type BooleanProps = {
  rive: Rive;
  input: StateMachineInput;
};

const BooleanInput: FC<BooleanProps> = ({ rive, input }) => {
  const isCheckedRef = useRef<boolean>(input.value as boolean);
  const [isChecked, setIsChecked] = useState<boolean>(input.value as boolean);

  function onCheckedChange(checked: boolean) {
    input.value = !input.value;
    setIsChecked(checked);
  }

  useEffect(() => {
    isCheckedRef.current = isChecked;
  }, [isChecked]);

  useEffect(() => {
    if (rive) {
      rive.on(EventType.Advance, () => {
        if (input.value !== isCheckedRef.current) {
          setIsChecked((isChecked) => !isChecked);
        }
      });
    }
  }, []);

  return (
    <div className={styles.Input}>
      <h4>{input.name}</h4>
      <CustomSwitch
        checked={isChecked}
        onChange={(_, checked) => onCheckedChange(checked)}
      />
    </div>
  );
};

type NumberProps = {
  rive: Rive;
  input: StateMachineInput;
  onDragChange: ValueCallback<boolean>;
};

const NumberInput: FC<NumberProps> = ({ rive, input, onDragChange }) => {
  const initialValueRounded = Math.floor(input.value as number);

  const inputRef = useRef<HTMLInputElement>(null);
  const originY = useRef<number>(0);
  const valueAtMoveStart = useRef<number>(initialValueRounded);

  const valueRef = useRef<number>(initialValueRounded);
  const [value, setValue] = useState<number>(initialValueRounded);
  // Store value as a string for display purposes to handle empty string case.
  const [valueStr, setValueStr] = useState<string>(
    initialValueRounded.toString()
  );

  const label = input.name;

  function updateValueOnMove(currentY: number) {
    // Divide by 2 to dampen the effect. This makes the value change by 1
    // for every 2 pixels moved
    const distance = (originY.current - currentY) / 2;
    onValueChange(valueAtMoveStart.current + distance);
    // Remove focus from input on change to keep it looking clean!
    inputRef.current!.blur();
  }

  // Mouse event handlers
  function onMouseDown(event: React.MouseEvent) {
    originY.current = event.pageY;
    valueAtMoveStart.current = value;
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    onDragChange(true);
  }

  function handleMouseMove(e: MouseEvent) {
    document.body.style.cursor = 'ns-resize';
    updateValueOnMove(e.pageY);
  }

  function handleMouseUp() {
    document.body.style.cursor = 'auto';
    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('mousemove', handleMouseMove);
    onDragChange(false);
  }

  // Touch event handlers
  function onTouchStart(event: React.TouchEvent) {
    originY.current = event.touches[0].pageY;
    valueAtMoveStart.current = value;
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchmove', handleTouchMove);
    onDragChange(true);
  }

  function handleTouchMove(e: TouchEvent) {
    e.preventDefault();
    e.stopPropagation();
    updateValueOnMove(e.changedTouches[0].pageY);
  }

  function handleTouchEnd() {
    document.removeEventListener('touchend', handleTouchEnd);
    document.removeEventListener('touchmove', handleTouchMove);
    onDragChange(false);
  }

  function onValueChange(value: number) {
    const flooredValue = Math.floor(value);
    setValue(flooredValue);
    setValueStr(flooredValue.toString());

    input.value = flooredValue;
  }

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    rive.on(EventType.Advance, () => {
      if (input.value !== valueRef.current) {
        const flooredValue = Math.floor(input.value as number);
        setValue(flooredValue);
        setValueStr(flooredValue.toString());
      }
    });
  }, []);

  return (
    <div className={styles.Input}>
      <h4>{label}</h4>
      <div className={styles.NumberInputControls}>
        <HoldButton
          className={styles.IncrementButton}
          onClick={() => onValueChange(value - 1)}
        >
          –
        </HoldButton>
        <input
          className={styles.NumberInput}
          ref={inputRef}
          name={label}
          type="number"
          value={valueStr}
          onChange={(e) => {
            if (e.target.value === '') {
              setValueStr(e.target.value);
            } else {
              onValueChange(Number(e.target.value));
            }
          }}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        />
        <HoldButton
          className={styles.IncrementButton}
          onClick={() => onValueChange(value + 1)}
        >
          +
        </HoldButton>
      </div>
    </div>
  );
};

type TriggerProps = {
  input: StateMachineInput;
};

const TriggerInput: FC<TriggerProps> = ({ input }) => {
  const [wasFired, setWasFired] = useState<boolean>(false);
  const label = input.name;
  return (
    <div className={styles.Input}>
      <h4>{label}</h4>
      <button
        className={join([
          styles.Trigger,
          ...insertIf(wasFired, styles.TriggerAnimation),
        ])}
        onAnimationEnd={() => setWasFired(false)}
        onClick={() => {
          setWasFired(true);
          input.fire();
        }}
      />
    </div>
  );
};
